import { action, computed, observable, reaction } from 'mobx';
import { UserExam } from 'types/exam';
import { getUserExams, getReporteesExamStatus } from '../services/ExamService';
import type { IRootStore } from './IRootStore';

type ExamStatusMap = {
  scheduled: UserExam[];
  unscheduled: UserExam[];
  results_pending: UserExam[];
  completed: UserExam[];
  cancelled: UserExam[];
  retake_available: UserExam[];
  retake_scheduled: UserExam[];
  retake_results_pending: UserExam[];
  retake_complete: UserExam[];
  noStatus: UserExam[];
};

class ExamsStore {
  rootStore: IRootStore;

  // set externally when viewing a specific exam
  code?: string;

  version?: string;

  @observable exams: UserExam[] = [];

  @observable loading = false;

  @observable enrollmentLoading = false;

  @observable canShowSuccessAlert = false;

  @observable intervalId: ReturnType<typeof setInterval> | undefined =
    undefined;

  @observable disableExamOperations = false;

  @observable interval = 10000;

  @observable reporteesExamStatus: UserExam[] = [];

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.enrollmentLoading,
      () => {
        if (!this.enrollmentLoading) {
          try {
            this.rootStore.userStore.getEnablements();
          } catch (e) {
            console.error(e);
          }
        }
      },
    );
  }

  @computed get exam(): UserExam | Record<string, never> {
    return (
      this.exams.find(
        (exam) => exam.exam_code === `${this.code}-${this.version}`,
      ) ?? {}
    );
  }

  @action getUserExams = async () => {
    this.loading = true;
    const examsInfo = await getUserExams();
    if (examsInfo) {
      this.exams = examsInfo;
    }
    this.disableExamOperations = false;
    this.loading = false;
  };

  @action getReporteesExamStatus = async (
    users: string[],
    signal?: AbortSignal,
  ) => {
    try {
      const examStatus = await getReporteesExamStatus(users, signal);
      this.reporteesExamStatus = examStatus;
    } catch (e: unknown) {
      const error = e as { name?: string; code?: string };
      if (
        error?.name === 'AbortError' ||
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED'
      ) {
        return this.reporteesExamStatus;
      }
      console.error('getReporteesExamStatus error: ', e);
    }
    return this.reporteesExamStatus;
  };

  @computed get examStatus(): ExamStatusMap {
    const examStatuses: ExamStatusMap = {
      scheduled: [],
      unscheduled: [],
      results_pending: [],
      completed: [],
      cancelled: [],
      retake_available: [],
      retake_scheduled: [],
      retake_results_pending: [],
      retake_complete: [],
      noStatus: [],
    };

    this.exams.forEach((exam) => {
      const statusKey = exam.status as keyof ExamStatusMap;
      if (statusKey && examStatuses[statusKey]) {
        examStatuses[statusKey].push(exam);
      } else {
        examStatuses.noStatus.push(exam);
      }
    });

    return examStatuses;
  }
}

export default ExamsStore;
